import {captureException} from '@sentry/browser';
import moment from 'moment';
import {
  A_DAY,
  BRANCH_MAPPING,
  BRANCH_NAME_MAPPING,
  DEFAULT_BRANCH,
  HTTP_STATUS,
  JJ_DOMAIN,
  MATCH_ALL_QUERY,
  PAGE_SIZE,
  SEARCH_ADVANCED_PARAMS,
  SEARCH_SORT_TYPE,
  SORT_TYPES,
  STANDARD_FORMAT,
} from '../constants/constants';
import {PANDG_FAQ_ITEMS} from '../constants/search.constants';
import {
  setBranch,
  setBranchOpeningTimes,
  setNearestBranch,
} from '../actions/branch.action';
import productApi from '../api/product.api';
import {
  checkProductDetails,
  getBranchFromUrl,
  getCategoryNamesFromUrl,
  getKeyCaseInsensitive,
  getSearchSeoTitle,
  getTodayDate,
  processedGQLSearchResults,
  processedProductDetail,
} from '../helpers/data.helper';
import {
  getFacetsFromServerSide,
  getSearchFromServerSide,
  getSearchPageFromServerSide,
  setProductDetail,
} from '../actions';
import SearchApi from '../api/search.api';
import {getApiConfig, parseConfig} from '../config/configProvider';
import {
  getFaqPageSchema,
  getOgData,
  getSchemaOpeningTimes,
} from '../helpers/seo.helper';
import {HALAL_FAQ_ITEMS} from '../constants/search.constants';
import {setCleanMode} from '../actions/cleanMode.action';
import {setShopBySectors} from '../actions/offers.action';
import {arrayToObject} from '../helpers/array.helper';
import {ServerCacheHelper} from '../helpers/ssr.helper';
import logo from '../images/logo.png';
import {setBlogCategoryBySlug, setBlogPost} from '../actions/blog.action';
import blogApi from '../api/blog.api';
import {strapiImage} from '../helpers/image.helper';
import jjContentApi from '../api/jjContent.api';
import {setJJContentBySlug} from '../actions/jjContent.action';
import {isGoogleBotRequest as isGoogleBotReq} from '../helpers/browser.helper';
import cmsApi from '../api/cms.api';
import {setBanner} from '../actions/cms.action';
import * as ActionTypes from '../actions/types.action';

// Product price cache — keyed by itemId, stores the last known complete pricing snapshot.
// Used to fill missing collection/delivery prices when the GraphQL response is partial,
// so the JSON-LD schema is always emitted with valid pricing data.
const PRODUCT_PRICE_CACHE_TTL = 48 * 60 * 60 * 1000; // 48h safety-net TTL

const productPriceCacheKey = itemId => `product_price_${itemId}`;

const isProductPriceDataComplete = product =>
  product &&
  product.itemId &&
  ((product.collection && product.collection.price != null) ||
    product.price != null) &&
  ((product.delivery && product.delivery.price != null) ||
    product.price != null) &&
  product.isAvailable !== undefined;

const updateProductPriceCache = product => {
  if (!product || !product.itemId) return;
  ServerCacheHelper.set(
    productPriceCacheKey(product.itemId),
    {
      collectionPrice:
        (product.collection && product.collection.price) ?? product.price,
      deliveryPrice:
        (product.delivery && product.delivery.price) ?? product.price,
      price: product.price,
      isAvailable: product.isAvailable,
      branches: product.branches,
    },
    PRODUCT_PRICE_CACHE_TTL
  );
};

const fillFromProductPriceCache = product => {
  if (!product || !product.itemId) return product;
  const cached = ServerCacheHelper.get(productPriceCacheKey(product.itemId));
  if (!cached) return product;

  const filled = {...product};

  if (!filled.collection || filled.collection.price == null) {
    filled.collection = {
      ...(filled.collection || {}),
      price: cached.collectionPrice,
    };
  }
  if (!filled.delivery || filled.delivery.price == null) {
    filled.delivery = {...(filled.delivery || {}), price: cached.deliveryPrice};
  }
  if (filled.price == null) {
    filled.price = cached.price;
  }
  if (filled.isAvailable === undefined && cached.isAvailable !== undefined) {
    filled.isAvailable = cached.isAvailable;
  }
  if (
    (!filled.branches || filled.branches.length === 0) &&
    cached.branches &&
    cached.branches.length > 0
  ) {
    filled.branches = cached.branches;
  }
  return filled;
};

// Forward the original client's IP to the GraphQL gateway on server-side-render calls. Without
// this, SSR requests reach the gateway (and graph-product-search) as the olympus pod IP, so
// per-client rate limiting and attribution downstream key on the wrong source — and all SSR
// traffic looks like one client. The gateway and graph-product-search already read these headers;
// this closes the chain at the SSR origin.
const forwardingHeaders = req => {
  const headers = {};
  const forwardedFor = req && req.headers && req.headers['x-forwarded-for'];
  const realIp = req && req.headers && req.headers['x-real-ip'];
  if (forwardedFor) {
    headers['x-forwarded-for'] = forwardedFor;
  }
  if (realIp) {
    headers['x-real-ip'] = realIp;
  }
  return headers;
};

export const loadProductDetailsData = async (match, store, req) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  const config = getApiConfig();
  const branchFromUrl = getBranchFromUrl(match);
  const deliveryDate = moment(new Date()).format(STANDARD_FORMAT);
  const isGoogleBotRequest = isGoogleBotReq(req);

  if (store) {
    store.dispatch(setBranch(branchFromUrl));
  }
  const isRunningInternalDefault =
    process.env.CONFIG_JS && process.env.NODE_LOCAL_DEV !== 'true';

  let productDetailDataAtFirstAvailableBranchPrice;

  try {
    const productWithFirstAvailableBranch = await productApi.getProduct(
      {
        itemIds: [match.params && match.params.itemId],
        deliveryDate,
        branch: '',
        host: isRunningInternalDefault
          ? parseConfig(process.env.CONFIG_JS).graphQlInternal
          : config.graphQL,
      },
      forwardingHeaders(req)
    );
    const firstAvailableBranch =
      productWithFirstAvailableBranch &&
      productWithFirstAvailableBranch.data &&
      productWithFirstAvailableBranch.data.searchProductsByItemId &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results
        .length > 0 &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches.length > 0 &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches[0] &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches[0].locationId;

    // The && chain above is `false` (not a branch) when the item is unknown or
    // stocked nowhere. Sending that as $branch fails gateway validation
    // ("String cannot represent a non string value: false"), so there is no
    // first-available-branch price to look up.
    if (firstAvailableBranch) {
      const productAtFirstAvailableBranch = await productApi.getProduct(
        {
          itemIds: [match.params && match.params.itemId],
          deliveryDate,
          branch: firstAvailableBranch,
          host: isRunningInternalDefault
            ? parseConfig(process.env.CONFIG_JS).graphQlInternal
            : config.graphQL,
        },
        forwardingHeaders(req)
      );
      const productDetailDataAtFirstAvailableBranch =
        processedProductDetail(
          productAtFirstAvailableBranch.data.searchProductsByItemId.results[0]
        ) || null;
      productDetailDataAtFirstAvailableBranchPrice =
        isGoogleBotRequest &&
        productDetailDataAtFirstAvailableBranch &&
        ((productDetailDataAtFirstAvailableBranch.collection &&
          productDetailDataAtFirstAvailableBranch.collection.price) ||
          (productDetailDataAtFirstAvailableBranch.delivery &&
            productDetailDataAtFirstAvailableBranch.delivery.price) ||
          productDetailDataAtFirstAvailableBranch.price);
    }
  } catch (e) {}

  try {
    const branchSpecificProductDetail = await productApi.getProduct(
      {
        itemIds: [match.params && match.params.itemId],
        deliveryDate,
        branch: branchFromUrl,
        host: isRunningInternalDefault
          ? parseConfig(process.env.CONFIG_JS).graphQlInternal
          : config.graphQL,
      },
      forwardingHeaders(req)
    );
    if (
      branchSpecificProductDetail &&
      branchSpecificProductDetail.errors &&
      branchSpecificProductDetail.errors.length > 0
    ) {
      return Promise.resolve({og: false});
    }
    if (checkProductDetails(branchSpecificProductDetail)) {
      const genericProductDetail = await productApi.getProduct(
        {
          itemIds: [match.params && match.params.itemId],
          deliveryDate,
          branch: '',
          host: isRunningInternalDefault
            ? parseConfig(process.env.CONFIG_JS).graphQlInternal
            : config.graphQL,
        },
        forwardingHeaders(req)
      );
      const genericProductDetailData = fillFromProductPriceCache(
        processedProductDetail(
          genericProductDetail.data.searchProductsByItemId.results[0]
        ) || null
      );
      if (isProductPriceDataComplete(genericProductDetailData)) {
        updateProductPriceCache(genericProductDetailData);
      }
      if (store && genericProductDetailData) {
        if (isGoogleBotRequest) {
          // On genericProductDetailData, not the raw genericProductDetail response:
          // the processed object is what gets dispatched, so setting it on the
          // response dropped the flag on every product the URL's branch does not
          // stock — exactly the case the branch rows describe.
          genericProductDetailData.isGoogleBotRequest = isGoogleBotRequest;
        }
        store.dispatch(setProductDetail(genericProductDetailData));
      }
      // Build a separate object for og/JSON-LD with synthesised prices — never mutate
      // the real product data that went into the Redux store, so the UI shows accurate pricing.
      const ogProductData = {
        ...genericProductDetailData,
        collection: genericProductDetailData.collection || {
          price:
            genericProductDetailData.price ||
            productDetailDataAtFirstAvailableBranchPrice,
        },
        delivery: genericProductDetailData.delivery || {
          price:
            genericProductDetailData.price ||
            productDetailDataAtFirstAvailableBranchPrice,
        },
      };
      const og = getOgData(ogProductData);
      return Promise.resolve(og);
    } else {
      const branchSpecificProductDetailData = fillFromProductPriceCache(
        processedProductDetail(
          branchSpecificProductDetail.data.searchProductsByItemId.results[0]
        ) || null
      );
      if (isProductPriceDataComplete(branchSpecificProductDetailData)) {
        updateProductPriceCache(branchSpecificProductDetailData);
      }
      if (isGoogleBotRequest) {
        branchSpecificProductDetailData.isGoogleBotRequest = isGoogleBotRequest;
      }
      if (store && branchSpecificProductDetailData) {
        store.dispatch(setProductDetail(branchSpecificProductDetailData));
      }
      // Build a separate object for og/JSON-LD with synthesised prices — never mutate
      // the real product data that went into the Redux store, so the UI shows accurate pricing.
      const ogProductData = {
        ...branchSpecificProductDetailData,
        collection: branchSpecificProductDetailData.collection || {
          price:
            branchSpecificProductDetailData.price ||
            productDetailDataAtFirstAvailableBranchPrice,
        },
        delivery: branchSpecificProductDetailData.delivery || {
          price:
            branchSpecificProductDetailData.price ||
            productDetailDataAtFirstAvailableBranchPrice,
        },
      };
      const og = getOgData(ogProductData);
      return Promise.resolve(og);
    }
  } catch (e) {
    console.log(e, 'ssr load data error');
    captureException(e);
    return Promise.resolve({og: false});
  }
};

export const loadSearchData = (match, store, req, isSpamUrl) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  let og;
  const config = getApiConfig();
  const host =
    process.env.CONFIG_JS && process.env.NODE_LOCAL_DEV !== 'true'
      ? parseConfig(process.env.CONFIG_JS).graphQlInternal
      : config.graphQL;
  const branchFromUrl = getBranchFromUrl(match);
  if (store && store.dispatch) {
    store.dispatch(setBranch(branchFromUrl));
  }
  const {brandName, category1Name, category2Name, category3Name} = match.params;
  const {
    q,
    listId,
    brandOwner,
    adTitle,
    sortType,
    first,
    brand,
    productFeatures,
    sizeOrCut,
    isNew,
    origin,
    category,
    categoryIds,
    advanced,
  } = (req && req.query) || {};
  let params = Object.assign({}, SEARCH_ADVANCED_PARAMS, {
    b: branchFromUrl,
    size: PAGE_SIZE.M,
    deliveryDate: getTodayDate(),
  });
  if (brandName) {
    params = {
      ...params,
      ...{
        brand: decodeURIComponent(brandName),
      },
    };
  }
  const categoryNames = getCategoryNamesFromUrl(
    category1Name,
    category2Name,
    category3Name
  );
  if (categoryNames.length > 0) {
    params = {
      ...params,
      ...{
        // Respect an explicit sortType from the URL (e.g. the user picked a
        // sort and reloaded/shared the link) — SSR must render the same
        // order client-side hydration will request, or the results visibly
        // re-sort right after load.
        sortType: sortType || SORT_TYPES.CATEGORY,
      },
      ...{
        categoryNames,
      },
    };
  } else if (listId) {
    // Not SEARCH_ADVANCED_PARAMS: the client-side request for this URL does
    // not send `advanced`, and SSR and hydration must ask the same question.
    // (`advanced` is inert on a listId browse — same count and order either
    // way — so this is about keeping the two requests identical, not about
    // scoring.)
    params = {
      b: branchFromUrl,
      size: PAGE_SIZE.M,
      deliveryDate: getTodayDate(),
      listId,
      brandOwner,
      // Mirrors SearchPage's own default (curated order unless the URL
      // overrides it) — see the same fallback in SearchPage.js.
      sortType: sortType || SEARCH_SORT_TYPE.LIST,
      q: MATCH_ALL_QUERY,
    };
  } else {
    params.q = q;
    // A typed search is a word search on the client: search.api.js sends `advanced` only when the
    // URL carries it. SSR started from SEARCH_ADVANCED_PARAMS and kept `advanced: true`, so it asked
    // product-search-svc a raw Lucene question instead - no synonyms, no minScore, no spelling
    // correction - and rendered different results (or a 404) from the ones hydration then showed.
    if (q && q !== MATCH_ALL_QUERY && advanced !== 'true') {
      delete params.advanced;
    }
    // Without this, SSR always fetched the backend's default order and
    // ignored the URL's own sortType, so the page visibly re-sorted the
    // moment client-side hydration re-requested with the right sortType.
    if (sortType) {
      params.sortType = sortType;
    }
    // These filters can all be applied to a plain keyword/wildcard search too
    // (no listId involved) — without forwarding them SSR ignores them and
    // renders the unfiltered results, which then visibly swap for the
    // filtered ones the moment client-side hydration re-requests with the
    // filter included. (search.api.js also relies on seeing these to know
    // whether an empty `q` is a valid filtered browse or a bare search.)
    if (brandOwner) {
      params.brandOwner = brandOwner;
    }
    if (first) {
      params.first = first;
    }
    if (brand) {
      params.brand = brand;
    }
    if (productFeatures) {
      params.productFeatures = productFeatures;
    }
    if (sizeOrCut) {
      params.sizeOrCut = sizeOrCut;
    }
    if (isNew) {
      params.isNew = isNew;
    }
    if (origin) {
      params.origin = origin;
    }
    if (category) {
      params.category = category;
    }
    if (categoryIds) {
      params.categoryIds = categoryIds;
    }
  }
  let httpCode = isSpamUrl ? HTTP_STATUS.NOT_FOUND : HTTP_STATUS.OK;
  try {
    const getSearch = SearchApi.search(params, forwardingHeaders(req), host);
    return Promise.all([getSearch]).then(responses => {
      if (
        responses[0] &&
        responses[0].data &&
        responses[0].data.searchProducts
      ) {
        if (responses[0].data.searchProducts.results) {
          const searchResults =
            processedGQLSearchResults(
              responses[0].data.searchProducts.results,
              responses[0].data.searchProducts.searchId
            ) || [];
          if (searchResults && searchResults.length === 0) {
            httpCode = HTTP_STATUS.NOT_FOUND;
          }
          // The server render shows the "Showing results for milk" banner too; without it a
          // corrected page would render milk under "mklk" with no explanation until hydration.
          store.dispatch(
            getSearchFromServerSide(
              searchResults,
              responses[0].data.searchProducts.correctedQuery
            )
          );
          store.dispatch(
            getSearchPageFromServerSide(responses[0].data.searchProducts.page)
          );
        }
        // featured (peer-gap trial) results are deliberately not read here: they are
        // account-specific and the SSR HTML is cacheable. SearchPage re-runs the search on mount
        // and the client-side response supplies them.
        if (responses[0].data.searchProducts.facets) {
          store.dispatch(
            getFacetsFromServerSide(responses[0].data.searchProducts.facets)
          );
        }
        const title = getSearchSeoTitle({
          brandName,
          categoryNames,
          listId,
          adTitle,
        });
        og = {
          og: true,
          title: `Buy Wholesale ${title} From JJ Foodservice For Delivery or Collection`,
          description: `View and order online from our range of wholesale ${title} at JJ Foodservice. Open to all.`,
          httpCode,
        };
        return Promise.resolve(og);
      }
      return Promise.resolve({
        httpCode,
      });
    });
  } catch (e) {
    captureException(e);
  }
};

export const loadSearchResultsData = title => {
  return {
    og: true,
    title: `Buy Wholesale ${title} From JJ Foodservice For Delivery or Collection`,
    description: `View and order online from our range of wholesale ${title} at JJ Foodservice. Open to all.`,
  };
};

export const loadHomePageData = (match, store, req) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  const hostDomain = 'http://olympus-app-internal:81';
  // const hostDomain = `${req.protocol}://localhost:8081`;
  return jjContentApi
    .getContentBySlug('shop-by-sectors', hostDomain)
    .then(res => {
      if (store && res) {
        store.dispatch(setShopBySectors(res));
      }
      return cmsApi
        .getBanners(hostDomain)
        .then(banners => {
          if (banners) {
            store.dispatch(setBanner(banners));
          }
        })
        .finally(() => {
          return Promise.resolve({});
        });
    })
    .catch(e => {
      captureException(e);
      return Promise.resolve({});
    });
};

export const loadBranchLocatorData = (match, store) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  const defaultOGData = {
    og: true,
    title:
      'JJ Foodservice Branch Finder - Find Your Nearest Branch For Wholesale Food & Catering Supplies in England',
    description:
      'Enter your postcode on our branch finder tool to find your nearest JJ Foodservice branch in England for collection orders.',
  };
  const branchId = getKeyCaseInsensitive(
    BRANCH_NAME_MAPPING,
    match.params.branchName
  );
  if (!branchId) {
    return Promise.resolve(defaultOGData);
  }
  const cachedopeningTimes = ServerCacheHelper.get('branches');
  const ogBranchData = ServerCacheHelper.get(`ogData-${branchId}`);
  // The blob load is asynchronous and can fail, so the cache is not guaranteed to
  // be populated. Without this guard arrayToObject calls reduce on null, which
  // throws and leaves the request hanging until the gateway times out.
  if (!cachedopeningTimes || cachedopeningTimes.length === 0) {
    return Promise.resolve(defaultOGData);
  }
  // Always the keyed object: consumers index this by branch code (BranchLocatorPage,
  // the product page's branch rows), so dispatching the raw array here would break
  // them on any request that hits the warm ogData cache.
  const openingTimes = arrayToObject(cachedopeningTimes, 'inventLocationId');
  store.dispatch(setBranchOpeningTimes(openingTimes));
  if (ogBranchData) {
    return Promise.resolve({...defaultOGData, ogBranchData});
  }

  const currentBranch = openingTimes[branchId];
  if (!currentBranch) {
    return Promise.resolve(defaultOGData);
  }
  store.dispatch(setNearestBranch(currentBranch));
  const newOgBranchData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `JJ Foodservice ${currentBranch.branchName}`,
    '@id': `https://jjfoodservice.com/branch/${(
      BRANCH_MAPPING[branchId] || BRANCH_MAPPING[DEFAULT_BRANCH]
    ).toLowerCase()}`,
    url: `https://jjfoodservice.com/branch/${(
      BRANCH_MAPPING[branchId] || BRANCH_MAPPING[DEFAULT_BRANCH]
    ).toLowerCase()}`,
    description:
      'Enter your postcode on our branch finder tool to find your nearest JJ Foodservice branch in England for collection orders.',
    telephone: currentBranch.contact && currentBranch.contact.phone,
    email: currentBranch.contact && currentBranch.contact.email,
    logo: {
      '@type': 'ImageObject',
      url: `https://${JJ_DOMAIN.PROD}${logo}`,
      width: 560,
      height: 92,
    },
    image: logo,
    currenciesAccepted: 'GBP',
    paymentAccepted: 'Cash, Credit Card',
    hasMap: `https://www.google.com/maps/search/JJ+Foodservice+Limited+${encodeURI(
      currentBranch.branchName
    )}/`,
    sameAs: [
      'https://www.facebook.com/jjfoodservice',
      'https://www.youtube.com/channel/UCt4UlV3a_3DW_-Zlrqn9EVQ',
    ],
    address: [
      {
        '@type': 'PostalAddress',
        streetAddress:
          currentBranch.address &&
          currentBranch.address.address.replace(/(\r\n|\n|\r)/gm, ', '),
        addressRegion: currentBranch.city,
        postalCode: currentBranch.address && currentBranch.address.zipcode,
        addressCountry: 'England',
      },
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: currentBranch.contact && currentBranch.contact.phone,
        contactType: 'customer service',
      },
    ],
    geo: {
      '@type': 'GeoCoordinates',
      latitude: currentBranch.latitude,
      longitude: currentBranch.longitude,
    },
    openingHoursSpecification:
      currentBranch.collectionOpeningTime &&
      getSchemaOpeningTimes(currentBranch.collectionOpeningTime),
  };
  ServerCacheHelper.set('openingTimes', openingTimes, A_DAY);
  ServerCacheHelper.set(`ogData-${branchId}`, newOgBranchData, A_DAY);
  return Promise.resolve({...defaultOGData, ogBranchData: newOgBranchData});
};

export const loadPackagingPageData = () => {
  return {
    og: true,
    title:
      'Buy Wholesale Takeaway Packaging Wholesales From JJ Foodservice For Delivery or Collection',
    description:
      'View and order online from our range of wholesale Takeaway Packaging at JJ Foodservice. Choose from a wide range of wholesale food packaging supplies & catering containers: takeaway boxes, bags, cups and more',
  };
};

export const loadHalalPageData = () => {
  return {
    og: true,
    title: 'Halal Wholesale Chicken, Meat & Doner | JJ Foodservice',
    description:
      'Halal wholesale chicken, lamb, beef, burgers and doner. For restaurants, takeaways and home customers. Next-day delivery or branch collection.',
    faqSchema: getFaqPageSchema(HALAL_FAQ_ITEMS),
  };
};

export const loadPandGPageData = () => {
  return {
    og: true,
    title: 'Professional Cleaning Chemicals | P&G PRO | JJ Foodservice',
    description:
      'Buy professional cleaning chemicals from the official UK P&G PRO distributor. Fairy, Flash, Deepio, Ariel and more, delivered with your food order.',
    faqSchema: getFaqPageSchema(PANDG_FAQ_ITEMS),
  };
};

export const loadNotFoundData = () => {
  return Promise.resolve({
    httpCode: 404,
  });
};

export const setAppCleanMode = (match, store) => store.dispatch(setCleanMode());

export const loadBlogCategories = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    // const hostDomain = `${req.protocol}://localhost:8081`;
    const categoriesHomeData = await blogApi.getAllBlogCategories(hostDomain);
    if (categoriesHomeData) {
      store.dispatch({
        type: ActionTypes.GET_BLOG_CATEGORIES_HOME_INFO_SUCCESS,
        categoriesHomeData,
      });
      return Promise.resolve({categories: true});
    }
    return Promise.resolve({categories: false});
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiBlogPostsBySlug = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    // const hostDomain = `${req.protocol}://localhost:8081`;
    const posts = await blogApi.getAllBlogCategoryBySlug(
      match.params.slugName,
      hostDomain
    );
    if (posts) {
      store.dispatch(setBlogCategoryBySlug(posts));
      return Promise.resolve({});
    }
    return Promise.resolve({});
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiPost = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    //const hostDomain = `${req.protocol}://${req.hostname}`;
    const blogPost = await blogApi.getBlogPostBySlug(
      match.params.postSlugName,
      hostDomain
    );
    if (blogPost) {
      store.dispatch(setBlogPost(blogPost));
      return Promise.resolve({
        og: true,
        title: blogPost && blogPost.title,
        description: blogPost && blogPost.excerpt,
        image: strapiImage(blogPost).full,
      });
    }
    return Promise.resolve({
      og: true,
      title: 'No blog post',
    });
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiContentPost = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    //const hostDomain = `${req.protocol}://${req.hostname}`;
    const contentPost = await blogApi.getContentPostBySlug(
      match.params.contentSlugName,
      hostDomain
    );
    if (contentPost) {
      store.dispatch(setJJContentBySlug(contentPost));
      return Promise.resolve({
        og: true,
        title: contentPost && contentPost.title,
        description: contentPost && contentPost.excerpt,
        image: strapiImage(contentPost).full,
      });
    }
    return Promise.resolve({
      og: true,
      title: 'No post found',
    });
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiPostPreview = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    const blogPost = await blogApi.getBlogPostBySlugPreview(
      match.params.postSlugName,
      hostDomain
    );
    if (blogPost) {
      store.dispatch(setBlogPost(blogPost));
      return Promise.resolve({
        og: true,
        title: blogPost && blogPost.title,
        description: blogPost && blogPost.excerpt,
        image: strapiImage(blogPost).full,
      });
    }
    return Promise.resolve({
      og: true,
      title: 'No blog post',
    });
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiContentPostPreview = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    const contentPost = await blogApi.getContentPostBySlugPreview(
      match.params.contentSlugName,
      hostDomain
    );
    if (contentPost) {
      store.dispatch(setJJContentBySlug(contentPost));
      return Promise.resolve({
        og: true,
        title: contentPost && contentPost.title,
        description: contentPost && contentPost.excerpt,
        image: strapiImage(contentPost).full,
      });
    }
    return Promise.resolve({
      og: true,
      title: 'No post found',
    });
  } catch (e) {
    captureException(e);
  }
};
